import React, { useState, useEffect } from 'react';
import { TrainingVideo, TrainingStats, videosAPI } from '../../services/api';
import VideoUpload from './VideoUpload';
import VideoEdit from './VideoEdit';
import {
  PlusIcon,
  PlayIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, CardHeader, Button, Badge, Spinner, StatCard, EmptyState, PageHero } from '../ui';

const VideoManagement: React.FC = () => {
  const [videos, setVideos] = useState<TrainingVideo[]>([]);
  const [stats, setStats] = useState<TrainingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [editingVideo, setEditingVideo] = useState<TrainingVideo | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [videosData, statsData] = await Promise.all([
        videosAPI.getAllForAdmin(),
        videosAPI.getStats(),
      ]);
      setVideos(videosData);
      setStats(statsData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    fetchData();
  };

  const handleToggleActive = async (video: TrainingVideo) => {
    try {
      await videosAPI.update(video.id, { isActive: !video.isActive });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update video');
    }
  };

  const handleDelete = async (video: TrainingVideo) => {
    if (!window.confirm(`Are you sure you want to delete "${video.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await videosAPI.delete(video.id);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete video');
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'Unknown';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Enhanced Hero Section */}
      <PageHero
        title="Training Video Management"
        subtitle="Manage training videos and track user progress"
        icon={<VideoCameraIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
        accentColor="pink"
      >
        <div className="mt-2">
          <Button
            variant="primary"
            onClick={() => setShowUpload(true)}
            leftIcon={<PlusIcon className="h-5 w-5" />}
          >
            Upload Video
          </Button>
        </div>
      </PageHero>

      {error && (
        <Card variant="bordered" className="border-error-200 bg-error-50">
          <CardBody className="p-4">
            <p className="text-sm text-error-600">{error}</p>
          </CardBody>
        </Card>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Videos"
            value={stats.totalVideos}
            icon={<PlayIcon className="h-6 w-6" />}
            variant="primary"
          />
          <StatCard
            title="Completed Users"
            value={stats.completedUsers}
            icon={<PlayIcon className="h-6 w-6" />}
            variant="success"
          />
          <StatCard
            title="Pending Users"
            value={stats.totalUsers - stats.completedUsers}
            icon={<PlayIcon className="h-6 w-6" />}
            variant="warning"
          />
          <StatCard
            title="Completion Rate"
            value={`${stats.completionRate.toFixed(1)}%`}
            icon={<PlayIcon className="h-6 w-6" />}
            variant="secondary"
          />
        </div>
      )}

      {/* Videos Table */}
      <Card variant="glass" hoverable clickable>
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Video
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Required
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {videos.map((video) => (
                  <tr key={video.id} className="hover:bg-neutral-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center mr-3">
                          <VideoCameraIcon className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-neutral-900">{video.title}</div>
                          {video.description && (
                            <div className="text-sm text-neutral-500 truncate max-w-xs">{video.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                      {formatDuration(video.duration)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="neutral">{video.order}</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={video.isActive ? 'success' : 'error'}>
                        {video.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={video.isRequired ? 'primary' : 'neutral'}>
                        {video.isRequired ? 'Required' : 'Optional'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(video)}
                          title={video.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {video.isActive ? (
                            <EyeSlashIcon className="h-4 w-4" />
                          ) : (
                            <EyeIcon className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingVideo(video)}
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(video)}
                          className="text-error-600 hover:text-error-700 hover:bg-error-50"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {videos.length === 0 && (
              <EmptyState
                title="No videos yet"
                description="Upload your first training video to get started."
                icon={<VideoCameraIcon className="h-12 w-12" />}
                action={
                  <Button
                    variant="primary"
                    onClick={() => setShowUpload(true)}
                    leftIcon={<PlusIcon className="h-5 w-5" />}
                  >
                    Upload Video
                  </Button>
                }
              />
            )}
          </div>
        </CardBody>
      </Card>

      {/* Upload Modal */}
      {showUpload && (
        <VideoUpload
          onUploadSuccess={handleUploadSuccess}
          onClose={() => setShowUpload(false)}
        />
      )}

      {/* Edit Modal */}
      {editingVideo && (
        <VideoEdit
          video={editingVideo}
          onUpdateSuccess={() => {
            fetchData();
            setEditingVideo(null);
          }}
          onClose={() => setEditingVideo(null)}
        />
      )}
    </div>
  );
};

export default VideoManagement;
